import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * UIContext - Manages UI state (panels, modals, etc.)
 */

const UIContext = createContext(null);

export function UIProvider({ children }) {
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(280);

  // Sidebar admin/settings panel — collapsible region at the bottom of the
  // sidebar. Without this, superadmins with demo mode enabled get a footer so
  // tall it pushes Favorites and conversations off-screen.
  const [adminPanelExpanded, setAdminPanelExpandedState] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('dp-admin-panel-expanded') === 'true';
    } catch { return false; }
  });
  const [adminPanelHeight, setAdminPanelHeightState] = useState(() => {
    if (typeof window === 'undefined') return 240;
    try {
      const v = parseInt(localStorage.getItem('dp-admin-panel-height') || '', 10);
      return Number.isFinite(v) && v >= 120 && v <= 600 ? v : 240;
    } catch { return 240; }
  });
  const setAdminPanelExpanded = useCallback((v) => {
    setAdminPanelExpandedState(v);
    try { localStorage.setItem('dp-admin-panel-expanded', String(v)); } catch { /* ignore */ }
  }, []);
  const setAdminPanelHeight = useCallback((v) => {
    const clamped = Math.max(120, Math.min(600, v));
    setAdminPanelHeightState(clamped);
    try { localStorage.setItem('dp-admin-panel-height', String(clamped)); } catch { /* ignore */ }
  }, []);
  const toggleAdminPanel = useCallback(() => {
    setAdminPanelExpanded(!adminPanelExpanded);
  }, [adminPanelExpanded, setAdminPanelExpanded]);

  // Artifact panel state
  const [artifactPanelOpen, setArtifactPanelOpen] = useState(false);
  const [artifactPanelWidth, setArtifactPanelWidth] = useState(400);
  const [currentArtifact, setCurrentArtifact] = useState(null);
  const [isEditingArtifact, setIsEditingArtifact] = useState(false);
  const [editedArtifactContent, setEditedArtifactContent] = useState(null);
  const [originalArtifactContent, setOriginalArtifactContent] = useState(null);
  const [isArtifactFullscreen, setIsArtifactFullscreen] = useState(false);

  // Artifact versioning state
  const [artifactVersionHistory, setArtifactVersionHistory] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const [isViewingOldVersion, setIsViewingOldVersion] = useState(false);

  // Modal states
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);

  // Prompt to insert into chat (SAT-79)
  const [pendingPromptInsert, setPendingPromptInsert] = useState(null);

  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  // Toggle artifact panel
  const toggleArtifactPanel = useCallback(() => {
    setArtifactPanelOpen(prev => !prev);
  }, []);

  // Open artifact in panel
  const showArtifact = useCallback((artifact) => {
    setCurrentArtifact(artifact);
    setArtifactPanelOpen(true);
    // Initialize version history with first version
    const initialVersion = {
      version: 1,
      content: artifact.content,
      timestamp: new Date().toISOString(),
      label: 'Initial version'
    };
    setArtifactVersionHistory([initialVersion]);
    setCurrentVersionIndex(0);
    setIsViewingOldVersion(false);
  }, []);

  // Close artifact panel
  const hideArtifact = useCallback(() => {
    setArtifactPanelOpen(false);
    setIsEditingArtifact(false);
    setEditedArtifactContent(null);
    setOriginalArtifactContent(null);
    setIsArtifactFullscreen(false);
    // Optionally clear artifact after animation
    setTimeout(() => setCurrentArtifact(null), 300);
  }, []);

  // Start editing artifact
  const startEditingArtifact = useCallback(() => {
    if (currentArtifact) {
      setOriginalArtifactContent(currentArtifact.content);
      setEditedArtifactContent(currentArtifact.content);
      setIsEditingArtifact(true);
    }
  }, [currentArtifact]);

  // Update edited artifact content
  const updateEditedContent = useCallback((newContent) => {
    setEditedArtifactContent(newContent);
  }, []);

  // Save artifact changes (creates new version)
  const saveArtifactChanges = useCallback(() => {
    if (currentArtifact && editedArtifactContent !== null) {
      // Create new version
      const newVersion = {
        version: artifactVersionHistory.length + 1,
        content: editedArtifactContent,
        timestamp: new Date().toISOString(),
        label: `Version ${artifactVersionHistory.length + 1}`
      };

      // Add to version history
      setArtifactVersionHistory(prev => [...prev, newVersion]);
      setCurrentVersionIndex(artifactVersionHistory.length); // Point to new version

      // Update current artifact
      setCurrentArtifact({
        ...currentArtifact,
        content: editedArtifactContent,
        version: newVersion.version
      });
      setOriginalArtifactContent(editedArtifactContent);
      setIsEditingArtifact(false);
      setIsViewingOldVersion(false);
    }
  }, [currentArtifact, editedArtifactContent, artifactVersionHistory.length]);

  // Revert artifact changes
  const revertArtifactChanges = useCallback(() => {
    if (originalArtifactContent !== null) {
      setEditedArtifactContent(originalArtifactContent);
    }
  }, [originalArtifactContent]);

  // Cancel editing
  const cancelEditingArtifact = useCallback(() => {
    setIsEditingArtifact(false);
    setEditedArtifactContent(null);
  }, []);

  // Toggle artifact fullscreen
  const toggleArtifactFullscreen = useCallback(() => {
    setIsArtifactFullscreen(prev => !prev);
  }, []);

  // Exit artifact fullscreen
  const exitArtifactFullscreen = useCallback(() => {
    setIsArtifactFullscreen(false);
  }, []);

  // View a specific artifact version
  const viewArtifactVersion = useCallback((versionIndex) => {
    if (versionIndex >= 0 && versionIndex < artifactVersionHistory.length) {
      const version = artifactVersionHistory[versionIndex];
      setCurrentVersionIndex(versionIndex);
      setIsViewingOldVersion(versionIndex < artifactVersionHistory.length - 1);

      // Update displayed artifact content to this version
      if (currentArtifact) {
        setCurrentArtifact({
          ...currentArtifact,
          content: version.content,
          version: version.version
        });
      }

      // Exit editing mode when switching versions
      setIsEditingArtifact(false);
      setEditedArtifactContent(null);
    }
  }, [artifactVersionHistory, currentArtifact]);

  // Restore an old version (makes it the new latest version)
  const restoreArtifactVersion = useCallback((versionIndex) => {
    if (versionIndex >= 0 && versionIndex < artifactVersionHistory.length) {
      const version = artifactVersionHistory[versionIndex];

      // Create a new version from the restored content
      const restoredVersion = {
        version: artifactVersionHistory.length + 1,
        content: version.content,
        timestamp: new Date().toISOString(),
        label: `Restored from v${version.version}`
      };

      // Add to version history
      setArtifactVersionHistory(prev => [...prev, restoredVersion]);
      setCurrentVersionIndex(artifactVersionHistory.length); // Point to new version

      // Update current artifact
      if (currentArtifact) {
        setCurrentArtifact({
          ...currentArtifact,
          content: version.content,
          version: restoredVersion.version
        });
      }

      setIsViewingOldVersion(false);
    }
  }, [artifactVersionHistory, currentArtifact]);

  // Go to latest version
  const goToLatestVersion = useCallback(() => {
    const latestIndex = artifactVersionHistory.length - 1;
    if (latestIndex >= 0) {
      viewArtifactVersion(latestIndex);
    }
  }, [artifactVersionHistory.length, viewArtifactVersion]);

  // Modal controls
  const openSettingsModal = useCallback(() => setSettingsModalOpen(true), []);
  const closeSettingsModal = useCallback(() => setSettingsModalOpen(false), []);

  const openShareModal = useCallback(() => setShareModalOpen(true), []);
  const closeShareModal = useCallback(() => setShareModalOpen(false), []);

  const openCommandPalette = useCallback(() => setCommandPaletteOpen(true), []);
  const closeCommandPalette = useCallback(() => setCommandPaletteOpen(false), []);
  const toggleCommandPalette = useCallback(() => {
    setCommandPaletteOpen(prev => !prev);
  }, []);

  // Prompt library controls (SAT-79)
  const openPromptLibrary = useCallback(() => setPromptLibraryOpen(true), []);
  const closePromptLibrary = useCallback(() => setPromptLibraryOpen(false), []);
  const insertPromptToChat = useCallback((promptText) => {
    setPendingPromptInsert(promptText);
    setPromptLibraryOpen(false);
  }, []);
  const clearPendingPromptInsert = useCallback(() => {
    setPendingPromptInsert(null);
  }, []);

  // Close all modals
  const closeAllModals = useCallback(() => {
    setSettingsModalOpen(false);
    setShareModalOpen(false);
    setCommandPaletteOpen(false);
    setPromptLibraryOpen(false);
  }, []);

  const value = {
    // Sidebar
    sidebarOpen,
    sidebarWidth,
    setSidebarOpen,
    setSidebarWidth,
    toggleSidebar,

    // Sidebar admin/settings panel (collapsible)
    adminPanelExpanded,
    adminPanelHeight,
    setAdminPanelExpanded,
    setAdminPanelHeight,
    toggleAdminPanel,

    // Artifact panel
    artifactPanelOpen,
    artifactPanelWidth,
    currentArtifact,
    setArtifactPanelOpen,
    setArtifactPanelWidth,
    toggleArtifactPanel,
    showArtifact,
    hideArtifact,

    // Artifact editing
    isEditingArtifact,
    editedArtifactContent,
    originalArtifactContent,
    startEditingArtifact,
    updateEditedContent,
    saveArtifactChanges,
    revertArtifactChanges,
    cancelEditingArtifact,

    // Artifact fullscreen
    isArtifactFullscreen,
    toggleArtifactFullscreen,
    exitArtifactFullscreen,

    // Artifact versioning
    artifactVersionHistory,
    currentVersionIndex,
    isViewingOldVersion,
    viewArtifactVersion,
    restoreArtifactVersion,
    goToLatestVersion,

    // Modals
    settingsModalOpen,
    shareModalOpen,
    commandPaletteOpen,
    promptLibraryOpen,
    openSettingsModal,
    closeSettingsModal,
    openShareModal,
    closeShareModal,
    openCommandPalette,
    closeCommandPalette,
    toggleCommandPalette,
    openPromptLibrary,
    closePromptLibrary,
    closeAllModals,

    // Prompt insert (SAT-79)
    pendingPromptInsert,
    insertPromptToChat,
    clearPendingPromptInsert,

    // Responsive
    isMobile,
    setIsMobile
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}

export default UIContext;
